import { useEffect } from "preact/hooks";
import { useLocation } from "preact-iso/router";

import {
  activeJobId,
  currentPage,
  filters,
  jobsPerPage,
} from "../store/appStore";
import type { Filters, SortOption, Timeframe } from "../lib/types";

/** Query-param keys we sync. */
const KEYS = {
  tab: "tab",
  q: "q",
  platform: "platform",
  timeframe: "timeframe",
  state: "state",
  company: "company",
  profile: "profile",
  tag: "tag",
  sort: "sort",
  page: "page",
  perPage: "per_page",
  job: "job",
  run: "run",
} as const;

const VALID_TIMEFRAMES: Timeframe[] = ["1h", "2h", "5h", "24h", "shortlisted", "all"];
const VALID_SORTS: SortOption[] = ["newest", "company", "score"];

function readURL(): Partial<Filters> & { tab?: string; page?: number; perPage?: number; job?: string } {
  const params = new URLSearchParams(window.location.search);
  const out: Partial<Filters> & { tab?: string; page?: number; perPage?: number; job?: string } = {};

  const q = params.get(KEYS.q);
  if (q !== null) out.q = q;

  const platform = params.get(KEYS.platform);
  if (platform) out.platform = platform as Filters["platform"];

  const timeframe = params.get(KEYS.timeframe);
  if (timeframe && VALID_TIMEFRAMES.includes(timeframe as Timeframe)) out.timeframe = timeframe as Timeframe;

  const state = params.get(KEYS.state);
  if (state) out.state = state;

  const company = params.get(KEYS.company);
  if (company) out.company = company;

  const profile = params.get(KEYS.profile);
  if (profile) out.profile = profile;

  const tag = params.get(KEYS.tag);
  if (tag) out.tag = tag;

  const sort = params.get(KEYS.sort);
  if (sort && VALID_SORTS.includes(sort as SortOption)) out.sort = sort as SortOption;

  const showDuplicates = params.get("duplicates");
  if (showDuplicates === "1") out.showDuplicates = true;

  const run = params.get(KEYS.run);
  if (run) out.runId = run;

  const tab = params.get(KEYS.tab) || undefined;
  if (tab) out.tab = tab;

  const page = params.get(KEYS.page);
  if (page) out.page = parseInt(page, 10) || 1;

  const perPage = params.get(KEYS.perPage);
  if (perPage) out.perPage = parseInt(perPage, 10) || 24;

  const job = params.get(KEYS.job);
  if (job) out.job = job;

  return out;
}

function writeURL(state: {
  tab: string;
  filters: Filters;
  page: number;
  perPage: number;
  jobId: string | null;
}) {
  const params = new URLSearchParams();
  const f = state.filters;

  if (state.tab && state.tab !== "jobs") params.set(KEYS.tab, state.tab);
  if (f.q) params.set(KEYS.q, f.q);
  if (f.platform && f.platform !== "all") params.set(KEYS.platform, f.platform);
  if (f.timeframe && f.timeframe !== "all") params.set(KEYS.timeframe, f.timeframe);
  if (f.state) params.set(KEYS.state, f.state);
  if (f.company) params.set(KEYS.company, f.company);
  if (f.profile) params.set(KEYS.profile, f.profile);
  if (f.tag) params.set(KEYS.tag, f.tag);
  if (f.sort && f.sort !== "newest") params.set(KEYS.sort, f.sort);
  if (f.showDuplicates) params.set("duplicates", "1");
  if (f.runId) params.set(KEYS.run, f.runId);
  if (state.page && state.page > 1) params.set(KEYS.page, String(state.page));
  if (state.perPage && state.perPage !== 24) params.set(KEYS.perPage, String(state.perPage));
  if (state.jobId) params.set(KEYS.job, state.jobId);

  const qs = params.toString();
  const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  if (newUrl !== `${window.location.pathname}${window.location.search}`) {
    window.history.replaceState({}, "", newUrl);
  }
}

/** Syncs the global filter/page/job signals to the URL query string and
 * vice versa. Call once at the app root. */
export function useUrlState() {
  const location = useLocation();

  // On mount (or path change): read URL into the store.
  useEffect(() => {
    const parsed = readURL();
    const nextFilters: Filters = {
      q: parsed.q ?? filters.value.q,
      platform: parsed.platform ?? filters.value.platform,
      timeframe: parsed.timeframe ?? filters.value.timeframe,
      state: parsed.state ?? filters.value.state,
      company: parsed.company ?? filters.value.company,
      profile: parsed.profile ?? filters.value.profile,
      tag: parsed.tag ?? filters.value.tag,
      sort: parsed.sort ?? filters.value.sort,
      showDuplicates: parsed.showDuplicates ?? filters.value.showDuplicates,
      runId: parsed.runId ?? filters.value.runId,
    };
    filters.value = nextFilters;
    if (parsed.page) currentPage.value = parsed.page;
    if (parsed.perPage) jobsPerPage.value = parsed.perPage;
    if (parsed.job) activeJobId.value = parsed.job;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.path]);

  // On store change: write URL.
  useEffect(() => {
    const write = () =>
      writeURL({
        tab: tabFromPath(location.path || "/"),
        filters: filters.value,
        page: currentPage.value,
        perPage: jobsPerPage.value,
        jobId: activeJobId.value,
      });
    write();
    // Subscribe to signal changes.
    const unsubs = [
      filters.subscribe(write),
      currentPage.subscribe(write),
      jobsPerPage.subscribe(write),
      activeJobId.subscribe(write),
    ];
    return () => unsubs.forEach((u) => u());
  }, [location.path]);
}

export function tabFromPath(path: string): string {
  if (path === "/" || path === "") return "jobs";
  if (path.startsWith("/pipeline")) return "pipeline";
  if (path.startsWith("/settings")) return "settings";
  return "jobs";
}
